/** The WIG's footprint on the ordinary project panel: one compact card, or one button.
 *
 * Deliberately small (Brian, 2026-09-29) — the goal, the headline number and the health
 * with its arithmetic, and a link to the lane. Everything else about the WIG lives on the
 * WIG board, so a project that is not a WIG looks exactly as it always did, apart from the
 * button a Portfolio Manager sees.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Target } from 'lucide-react'

import { Button, Can } from '@/components/ui'
import { Permissions } from '@/lib/session'
import { GoalLine } from '@/features/wig/WigLane'
import { HealthChip, ScoreBar } from '@/features/wig/WigParts'
import { WigWizard } from '@/features/wig/WigWizard'
import { formatNumber, towards, useProjectWig } from '@/features/wig/useWig'

export function ProjectWigCard({ projectId, isWig, isLive }: { projectId: string; isWig: boolean; isLive: boolean }) {
  const { data: wig } = useProjectWig(projectId, isWig)
  const [wizard, setWizard] = useState(false)

  if (!isWig) {
    if (!isLive) return null
    return (
      <Can permission={Permissions.projectsCreate}>
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-border px-4 py-3">
          <Target size={16} aria-hidden style={{ color: 'var(--brand)' }} />
          <p className="min-w-0 flex-1 text-xs text-fg-muted">
            Run this project as a Wildly Important Goal — a goal line, weekly measures and
            commitments on top of what is here. Nothing is recreated.
          </p>
          <Button onClick={() => setWizard(true)}>Make this a WIG</Button>
        </div>
        {wizard ? <WigWizard projectId={projectId} onClose={() => setWizard(false)} /> : null}
      </Can>
    )
  }

  if (!wig) return null
  const headline = wig.lag[0]
  return (
    <section className="card space-y-3 p-4" style={{ borderLeft: '3px solid var(--brand)' }}>
      <div className="flex items-center gap-2">
        <Target size={15} aria-hidden style={{ color: 'var(--brand)' }} />
        <span className="micro-label text-fg-muted">Wildly important goal · owned by {wig.owner.displayName}</span>
        <span className="flex-1" />
        <HealthChip health={wig.health} />
      </div>
      <GoalLine lane={wig} />
      {headline ? (
        <div>
          <div className="mb-1 flex items-baseline justify-between text-sm">
            <span className="text-fg-muted">{headline.name}</span>
            <span className="font-heading text-lg text-fg">
              {formatNumber(headline.current)}
              <span className="text-sm text-fg-muted"> {towards(headline)}</span>
            </span>
          </div>
          <ScoreBar lag={headline} />
        </div>
      ) : null}
      <p className="text-xs text-fg-muted">
        {wig.governsHealth
          ? "This project's health is set by the WIG's pace, not by hand."
          : 'Health stays a person’s call until the WIG has a lag and a lead measure.'}
      </p>
      <Link
        to={`/wig?team=${wig.team}`}
        className="inline-flex items-center gap-1 text-sm font-semibold text-fg hover:underline"
      >
        Open its lane on the WIG board <ArrowRight size={14} aria-hidden />
      </Link>
    </section>
  )
}
